import {
    Component,
    OnInit,
    Input,
    Output,
    HostBinding,
    EventEmitter,
    Inject,
    ElementRef,
    forwardRef,
    AfterViewInit,
    ViewChild,
} from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { UniqueSelectionDispatcher } from '@angular/cdk/collections';

export const ICC_RADIO_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => RadioComponent),
    multi: true,
};
@Component({
    selector: 'icc-radio',
    templateUrl: './radio.component.html',
    styleUrls: ['./radio.component.scss'],
    providers: [ICC_RADIO_CONTROL_VALUE_ACCESSOR],
})
export class RadioComponent implements OnInit, ControlValueAccessor, AfterViewInit {
    private _checked = false;
    private _disabled = false;
    private _value: string | number | boolean = '';
    private _required = false;
    private _id: string | null = null;
    private _name = 'iccRadio';
    private _size: 'large' | 'medium' | 'small' = 'large';

    @ViewChild('input') input!: ElementRef<HTMLElement>;

    @Input()
    @HostBinding('attr.name')
    get name(): string {
        return this._name;
    }
    set name(name: string) {
        this._name = name ? name : 'iccRadio';
    }

    @Input()
    @HostBinding('attr.id')
    get id(): string | null {
        return this._id;
    }
    set id(id: string | null) {
        this._id = id;
    }

    @Input()
    @HostBinding('attr.value')
    get value(): string | number | boolean {
        return this._value;
    }
    set value(value: string | number | boolean) {
        this._value = value;
    }

    @Input()
    @HostBinding('class.icc-checked')
    get checked() {
        return this._checked;
    }
    set checked(_checked: any) {

        const newChecked = coerceBooleanProperty(_checked);
        if (this._checked  !== newChecked) {
            this._checked = newChecked;
            if (newChecked && this.id) {
            // Notify all radio buttons with the same name to un-check.
            this._radioDispatcher.notify(this.id, this.name);
            }
        }
    }

    @Input()
    @HostBinding('class.icc-disabled')
    get disabled() {
        return this._disabled;
    }
    set disabled(value: any) {
        this._disabled = coerceBooleanProperty(value);
    }

    @HostBinding('attr.disabled')
    get nativeDisabled(): boolean {
        return this.disabled;
    }

    @Input()
    @HostBinding('attr.required')
    get required(): boolean {
        return this._required;
    }
    set required(required: boolean) {
        this.required = required;
    }

    @Input()
    get size() {
        return this._size;
    }
    set size(size: 'large' | 'medium' | 'small') {
        this._size = size;
    }

    @Output()
    readonly valueChange = new EventEmitter<string | number | boolean>();

     // eslint-disable-next-line @typescript-eslint/no-empty-function
     _onTouched: () => any = () => {};
     // eslint-disable-next-line @typescript-eslint/no-empty-function
     private _controlValueAccessorChangeFn: (checked: any) => void = () => {};

    /** Unregister function for _radioDispatcher */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private _removeUniqueSelectionListener: () => void = () => {};

    constructor(
        @Inject(ElementRef) 
            private readonly _elementRef: ElementRef<HTMLElement>,
            private _focusMonitor: FocusMonitor,
            private _radioDispatcher: UniqueSelectionDispatcher
        ) {

            this._removeUniqueSelectionListener =
                _radioDispatcher.listen((id: string, name: string) => {
                    if (id !== this.id && name === this.name) {
                        this.checked = false;
                    }
            });


        }

    ngOnInit() {
        this.handleSize();
        this.handleClasses();
    }

    ngAfterViewInit() {

        this._focusMonitor.monitor(this._elementRef, true).subscribe((focusOrigin: FocusOrigin) => {
            if (!focusOrigin) {
                // When a focused element becomes disabled, the browser *immediately* fires a blur event.
                // Angular does not expect events to be raised during change detection, so any state change
                // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
                // See https://github.com/angular/angular/issues/17793. To work around this, we defer
                // telling the form control it has been touched until the next tick.
                Promise.resolve().then(() => {
                    this._onTouched();
                });
            }
        });


    }

    writeValue(value: any): void {
        this.checked = value === this.value;
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    registerOnChange(fn: (_: any) => {}): void {
        this._controlValueAccessorChangeFn = fn;
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    registerOnTouched(fn: () => {}): void {
        this._onTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    /** Focuses the checkbox. */
    focus(origin?: FocusOrigin, options?: FocusOptions): void {
        if(this.input){
            if (origin) {
                this._focusMonitor.focusVia(this.input, origin, options);
            } else {
                this.input.nativeElement.focus(options);
            }
        }
    }

    protected handleSize() {
        if (this._elementRef?.nativeElement?.classList?.value) {
            const classes = this._elementRef?.nativeElement?.classList;
            if (classes.value.includes('icc-size-large')) {
                this._size = 'large';
            }
            if (classes.value.includes('icc-size-medium')) {
                this._size = 'medium';
            }
            if (classes.value.includes('icc-size-small')) {
                this._size = 'small';
            }
        }
    }

    protected handleClasses() {
        if (this._elementRef?.nativeElement) {
            const element = this._elementRef.nativeElement as HTMLElement;

            element.classList.add('icc-size-' + this._size);

            if (this._disabled) {
                element.classList.add('icc-disabled');
            }
            if (this._checked) {
                element.classList.add('icc-checked');
            }
        }
    }

    _onInputChange(event: Event) {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();
        this.checked = true;
        this.valueChange.emit(this._value);
        this._controlValueAccessorChangeFn(this._value);

      }


    _onInputClick(event: Event) {
        // We have to stop propagation for click events on the visual hidden input element.
        // By default, when a user clicks on a label element, a generated click event will be
        // dispatched on the associated input element. Since we are using a label element as our
        // root container, the click event on the `radio-button` will be executed twice.
        // The real click event will bubble up, and the generated click event also tries to bubble up.
        // This will lead to multiple click events.
        // Preventing bubbling for the second event will solve that issue.
        event.stopPropagation();
      }
}
